import { element, showMessage } from './publicUi.js';
import { getLocale } from '../services/i18n.js';

const statuses = [['NEW', 'Новое'], ['READ', 'Прочитано'], ['ARCHIVED', 'Архив'], ['SPAM', 'Спам']];

export function feedbackDeliveryText(delivery) {
  if (!delivery) return 'Email-уведомление выключено';
  const labels = {
    PENDING: delivery.lastError === 'DELIVERY_FAILED' ? 'Ошибка доставки; повтор запланирован' : 'Ожидает отправки',
    SENDING: 'Отправляется', SENT: 'Доставлено',
    FAILED: delivery.lastError === 'EXPIRED' ? 'Не доставлено: срок ожидания истёк' : 'Не доставлено после повторов',
  };
  const attempts = Number.isInteger(delivery.attempts) && delivery.attempts > 0 ? ` · попыток: ${delivery.attempts}` : '';
  const date = new Date(delivery.updatedAt);
  const updated = Number.isNaN(date.getTime()) ? '' : ` · обновлено ${date.toLocaleString(getLocale())}`;
  return `Email: ${labels[delivery.state] ?? 'состояние доставки неизвестно'}${attempts}${updated}`;
}

export class FeedbackEditor {
  constructor({ item, save, remove, saved, denied, cancel, checkList }) {
    Object.assign(this, { item, save, remove, saved, denied, cancel, checkList });
  }
  render() {
    const form = element('form', 'spec-box post-editor'); this.form = form;
    form.append(element('h2', 'spec-title', this.item.subject));
    const sender = this.item.user ? `${this.item.sender} · аккаунт ${this.item.user.username} (${this.item.user.uid})` : this.item.sender;
    form.append(element('p', '', `${sender} · ${new Date(this.item.createdAt).toLocaleString(getLocale())} · версия ${this.item.version}`));
    form.append(element('p', 'profile-text', feedbackDeliveryText(this.item.delivery)));
    const message = element('pre', 'profile-text', this.item.message); message.style.overflowWrap = 'anywhere'; message.style.whiteSpace = 'pre-wrap';
    form.append(message);
    this.status = element('select', 'auth-input'); this.status.name = 'status'; this.status.setAttribute('aria-label', 'Статус обращения');
    for (const [value, label] of statuses) { const option = element('option', '', label); option.value = value; this.status.append(option); }
    [...this.status.options].find(option => option.value === this.item.status)?.setAttribute('selected', '');
    const statusLabel = element('label', 'auth-form-label', 'Статус обращения'); statusLabel.append(this.status); form.append(statusLabel);
    this.submitButton = element('button', 'filter-btn', 'Сохранить статус');
    this.cancelButton = element('button', 'filter-btn', 'Отменить и вернуться к списку'); this.cancelButton.type = 'button';
    this.cancelButton.addEventListener('click', () => { if (!this.busy && !this.disposed) this.cancel(); });
    this.feedback = element('div'); this.feedback.setAttribute('role', 'status');
    this.checkListButton = element('button', 'filter-btn', 'Обновить список без потери выбора'); this.checkListButton.type = 'button'; this.checkListButton.hidden = true;
    this.checkListButton.addEventListener('click', () => { if (!this.busy && !this.disposed) void this.checkList?.(); });
    form.append(this.submitButton, this.cancelButton, this.feedback, this.checkListButton);
    this.deleteButton = element('button', 'filter-btn admin-delete-button', 'Удалить обращение'); this.deleteButton.type = 'button';
    this.deleteConfirmation = element('div', 'public-message'); this.deleteConfirmation.hidden = true;
    this.deleteConfirmation.append(element('p', '', `Удалить обращение «${this.item.subject}» (версия ${this.item.version}) без возможности восстановления?`));
    const confirm = element('button', 'filter-btn admin-delete-button', 'Да, удалить обращение'), back = element('button', 'filter-btn', 'Оставить обращение');
    confirm.type = back.type = 'button';
    this.deleteButton.addEventListener('click', () => {
      if (this.busy || this.completed || this.disposed || this.uncertain) return;
      this.deleteConfirmation.hidden = false; back.focus();
    });
    back.addEventListener('click', () => { if (!this.busy) { this.deleteConfirmation.hidden = true; this.deleteButton.focus(); } });
    confirm.addEventListener('click', () => { void this.removeItem(); });
    this.deleteConfirmation.append(confirm, back); form.append(this.deleteButton, this.deleteConfirmation);
    form.addEventListener('submit', event => { event.preventDefault(); void this.submit(); });
    return form;
  }
  setBusy(value) {
    this.busy = value;
    for (const control of this.form.querySelectorAll('select,button')) control.disabled = value || !!this.completed || (!!this.uncertain && control !== this.cancelButton && control !== this.checkListButton);
  }
  async submit() {
    if (this.busy || this.disposed || this.completed || this.uncertain || this.deleteConfirmation.hidden === false) return;
    if (!statuses.some(([value]) => value === this.status.value)) { showMessage(this.feedback, 'Выберите статус обращения.'); return; }
    this.setBusy(true); showMessage(this.feedback, 'Сохранение статуса…');
    try {
      const result = await this.save(this.item.id, this.item.version, this.status.value); this.completed = true;
      if (!this.disposed) { showMessage(this.feedback, 'Статус сохранён.'); this.saved(result); }
    } catch (error) { this.handleFailure(error, 'сохранения'); }
    finally { this.busy = false; if (!this.disposed) this.setBusy(false); }
  }
  async removeItem() {
    if (this.busy || this.disposed || this.completed || this.uncertain || this.deleteConfirmation.hidden !== false) return;
    this.setBusy(true); showMessage(this.feedback, 'Удаление…');
    try {
      await this.remove(this.item.id, this.item.version); this.completed = true;
      if (!this.disposed) { showMessage(this.feedback, 'Обращение удалено.'); this.saved(); }
    } catch (error) {
      if (!this.disposed && error.status === 404) { this.completed = true; showMessage(this.feedback, 'Обращение уже отсутствует.'); this.saved(); }
      else this.handleFailure(error, 'удаления');
    } finally {
      this.busy = false;
      if (!this.disposed) { this.deleteConfirmation.hidden = true; this.setBusy(false); }
    }
  }
  handleFailure(error, operation) {
    if (this.disposed) return;
    if (error.status === 401 || (error.status === 403 && error.code !== 'CSRF_INVALID')) { this.denied(error); return; }
    this.uncertain = !error.status || error.status >= 500 || error.code === 'UNAVAILABLE';
    this.checkListButton.hidden = !this.uncertain || !this.checkList;
    showMessage(this.feedback, this.uncertain
      ? `Результат ${operation} неизвестен. Выбор сохранён. Обновите список и откройте обращение заново перед следующей операцией.`
      : error.message);
  }
  dispose() { this.disposed = true; }
}
